import type { ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";
import type { AccessibleAppSummary } from "@/app/types";
import { Button } from "@/components/ui/button";
import type { User } from "@/app/types";

interface AppShellProps {
  user: User;
  accessibleApps?: AccessibleAppSummary[];
  onLogout: () => Promise<void> | void;
  children: ReactNode;
}

function navClass({ isActive }: { isActive: boolean }) {
  return isActive
    ? "text-foreground font-medium border-b border-foreground pb-1"
    : "text-muted-foreground hover:text-foreground transition-colors pb-1";
}

export function AppShell({ user, accessibleApps = [], onLogout, children }: AppShellProps) {
  const canSeeScreenBudget = user.isAdmin || accessibleApps.some(app => app.serviceKey === "screen-budget");

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-20 border-b bg-background/90 backdrop-blur">
        <div className="container mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-3">
          <Link to="/dashboard" className="text-sm font-semibold tracking-wide">
            Services
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <NavLink to="/dashboard" className={navClass}>
              Dashboard
            </NavLink>
            {canSeeScreenBudget ? (
              <NavLink to="/apps/screen-budget" className={navClass}>
                Screen Budget
              </NavLink>
            ) : null}
            {user.isAdmin ? (
              <NavLink to="/admin" className={navClass}>
                Admin
              </NavLink>
            ) : null}
          </nav>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">
              {user.username} · {user.isAdmin ? "Admin" : "User"}
            </span>
            <Button variant="outline" size="sm" onClick={() => void onLogout()}>
              Log out
            </Button>
          </div>
        </div>
      </header>
      <main className="container mx-auto max-w-6xl px-6 py-6 md:py-8">{children}</main>
    </div>
  );
}
